// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Platform, Pressable, StyleSheet, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useI18n } from '@/i18n';
import { isBetaBadgeEnabled } from '@/services/betaFeature';
import { useBetaInfoState } from '@/stores/betaInfoState';
import appColors from '@/theme/colors';

type BetaBadgeProps = {
  style?: StyleProp<ViewStyle>;
  /**
   * Narrow-web header, where the centred screen title is absolutely positioned over the full
   * header width and so runs underneath whatever sits on the left. Shrinking the pill buys the
   * title back the room it needs.
   */
  compact?: boolean;
};

/**
 * App-wide "Goffy is in beta" marker, tappable for the explainer. Gates itself on the kill switch
 * so every call site stays one unconditional line and none of them can forget the flag.
 *
 * The pill styling is deliberately the same recipe as the hole-view beta pill in
 * features/hole/holeview/HoleViewModal.tsx. That badge makes the narrower claim that the hole view
 * itself is in beta, so the two are separate concepts that happen to share a look; if they ever
 * need to move together, extract the styles then.
 */
export function BetaBadge({ style, compact = false }: BetaBadgeProps) {
  const { t } = useI18n();
  const setOpen = useBetaInfoState((state) => state.setOpen);

  if (!isBetaBadgeEnabled()) {
    return null;
  }

  return (
    <Pressable
      testID="beta-badge"
      accessibilityRole="button"
      accessibilityLabel={t('beta.badgeLabel')}
      onPress={() => setOpen(true)}
      style={[styles.pill, compact && styles.pillCompact, style]}
      // The pill is ~18pt tall, well under the 44pt touch target minimum.
      hitSlop={8}
    >
      <Text style={[styles.pillText, compact && styles.pillTextCompact]}>{t('beta.badge')}</Text>
    </Pressable>
  );
}

/**
 * Placement for the screens that render with `headerShown: false` and have no chrome row of their
 * own for the badge to sit in (index, login, register). Absolutely positioned in the app shell so
 * it stays put while the screen underneath scrolls.
 */
export function BetaBadgeOverlay() {
  const insets = useSafeAreaInsets();

  if (!isBetaBadgeEnabled()) {
    return null;
  }

  return (
    <View style={[styles.overlay, { top: insets.top + 8 }]} pointerEvents="box-none">
      <BetaBadge />
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.accent.goldBorder,
    backgroundColor: appColors.accent.goldSoft,
    ...Platform.select({
      web: { cursor: 'pointer' },
      default: {},
    }),
  },
  pillCompact: {
    paddingHorizontal: 4,
    paddingVertical: 1,
  },
  pillText: {
    color: appColors.accent.gold,
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  pillTextCompact: {
    fontSize: 9,
    letterSpacing: 0.5,
  },
  overlay: {
    position: 'absolute',
    left: 12,
    zIndex: 10,
  },
});
